import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PRODUCTS, PRODUCT_ORDER, type Product } from '../lib/products';

const COLOR_MAP: Record<string, { bg: string; border: string; text: string; light: string; btn: string; gradient: string }> = {
  blue: {
    bg: 'bg-blue-600',
    border: 'border-blue-600',
    text: 'text-blue-600',
    light: 'bg-blue-50',
    btn: 'bg-blue-600 hover:bg-blue-700',
    gradient: 'from-blue-500 to-blue-600',
  },
  purple: {
    bg: 'bg-purple-600',
    border: 'border-purple-600',
    text: 'text-purple-600',
    light: 'bg-purple-50',
    btn: 'bg-purple-600 hover:bg-purple-700',
    gradient: 'from-purple-500 to-purple-600',
  },
  green: {
    bg: 'bg-green-600',
    border: 'border-green-600',
    text: 'text-green-600',
    light: 'bg-green-50',
    btn: 'bg-green-600 hover:bg-green-700',
    gradient: 'from-green-500 to-green-600',
  },
  orange: {
    bg: 'bg-orange-500',
    border: 'border-orange-500',
    text: 'text-orange-500',
    light: 'bg-orange-50',
    btn: 'bg-orange-500 hover:bg-orange-600',
    gradient: 'from-orange-400 to-orange-500',
  },
};

function NotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">产品未找到</h1>
        <Link to="/pricing" className="text-blue-600 hover:underline">返回定价页</Link>
      </div>
    </div>
  );
}

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', company: '', phone: '', need: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const product = productId ? PRODUCTS[productId] : null;

  if (!product) return <NotFound />;

  const colors = COLOR_MAP[product.color] || COLOR_MAP.blue;

  const faqs = [
    {
      q: `智小客和人工客服如何切换？`,
      a: '当AI无法解答时，系统会自动转接人工客服，并携带完整对话上下文，确保客户体验无缝衔接。',
    },
    {
      q: `需要多长时间完成部署？`,
      a: '标准产品1-3个工作日完成配置和上线。定制服务根据复杂度，一般1-4周完成交付。',
    },
    {
      q: `支持私有化部署吗？`,
      a: '企业版及以上支持私有化部署，确保数据完全存储在您的自有服务器上，满足高合规要求。',
    },
    {
      q: `如何保证回答的准确性？`,
      a: '我们采用RAG检索增强生成技术，结合企业知识库，确保回答基于真实业务信息，并持续优化模型。',
    },
  ];

  // Filter FAQs to relevant ones per product (simplified - show all for demo)
  const relevantFaqs = productId === 'custom'
    ? [
        { q: '定制周期一般多长？', a: '根据需求复杂度，入门定制约2-4周，深度定制约1-3个月，战略合作项目另行约定。' },
        { q: '可以先做概念验证吗？', a: '可以。我们提供免费需求调研和方案初稿，帮助您评估可行性和ROI。' },
        { q: '后续迭代如何收费？', a: '年度维保包含小幅迭代，大幅功能增加单独计费，收费标准提前确认。' },
        { q: '数据安全如何保障？', a: '私有化部署确保数据不出您的网络，支持国产加密标准，提供安全审计报告。' },
      ]
    : productId === 'xiaoxiaobang'
    ? [
        { q: '如何提升询盘转化率？', a: 'AI会分析每条线索的成交意向度，优先推荐高价值客户，并提供针对性话术建议。' },
        { q: '可以对接CRM系统吗？', a: '支持Salesforce、简道云、纷享销客等主流CRM，以及企业自有CRM的API对接。' },
        { q: '销售话术如何管理？', a: '企业可自定义话术库，AI会学习优秀销售的话术，并分析每个人的话术效果。' },
        { q: '数据会泄露给竞争对手吗？', a: '所有数据均为贵司私有，我们承诺不将您的业务数据用于任何其他客户或AI训练。' },
      ]
    : productId === 'banxiaozhu'
    ? [
        { q: '会议纪要准确率如何？', a: '基于 Whisper 语音识别 + 大语言模型，会议纪要准确率达95%以上，关键信息提取准确。' },
        { q: '审批流可以自定义吗？', a: '支持拖拽式审批流配置，可设置多级审批、条件分支、超时自动通过等复杂逻辑。' },
        { q: '知识库支持哪些文档格式？', a: '支持PDF、Word、PPT、Excel、TXT、Markdown等常见格式，支持图片和表格的智能解析。' },
        { q: '可以私有化部署吗？', a: '企业版支持私有化部署，部署在您的内网环境，数据完全自主可控。' },
      ]
    : [
        { q: '可以先试用吗？', a: '提供15天免费演示期，全功能体验。首月5折优惠，降低您的决策风险。' },
        { q: '如何保证回答的准确性？', a: '采用RAG检索增强生成技术，结合企业知识库，持续优化模型效果。' },
        { q: '人工接管方便吗？', a: 'AI无法解答时自动转接人工，携带完整上下文，确保无缝切换。' },
        { q: '支持哪些渠道接入？', a: '支持微信公众号、微信小程序、企业微信、网站H5、APP等多渠道接入。' },
      ];

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
  };

  const prevProduct = productId ? PRODUCT_ORDER[PRODUCT_ORDER.indexOf(productId) - 1] : null;
  const nextProduct = productId ? PRODUCT_ORDER[PRODUCT_ORDER.indexOf(productId) + 1] : null;

  return (
    <div className="min-w-0">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">智</span>
              </div>
              <span className="text-xl font-bold text-gray-900">智纬AI</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#products" className="text-gray-600 hover:text-gray-900 text-sm font-medium">产品</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">定价</Link>
              <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">功能</a>
              <a href="#pricing-tiers" className="text-gray-600 hover:text-gray-900 text-sm font-medium">套餐</a>
              <a href="#contact" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                立即咨询
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={`bg-gradient-to-br ${colors.light} py-16 overflow-hidden`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-14 h-14 ${colors.bg} rounded-2xl flex items-center justify-center text-3xl shadow-lg`}>
                  {product.emoji}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                  <p className="text-gray-500">{product.tagline}</p>
                </div>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">{product.description}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className={`px-6 py-3 ${colors.btn} text-white rounded-xl font-semibold text-center`}>
                  预约免费演示
                </a>
                <a href="#pricing-tiers" className="px-6 py-3 bg-white text-gray-700 rounded-xl font-semibold text-center border border-gray-200 hover:bg-gray-50 transition">
                  查看定价
                </a>
              </div>
            </div>
            <div className="relative">
              <div className={`w-full h-64 ${colors.light} rounded-2xl border-2 border-${product.color}-200 flex items-center justify-center`}>
                <div className="text-center">
                  <div className="text-6xl mb-4">{product.emoji}</div>
                  <div className="text-xl font-bold text-gray-900">{product.name}</div>
                  <div className="text-gray-500 text-sm">{product.tagline}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">核心功能</h2>
            <p className="text-gray-600 max-w-xl mx-auto">专为{product.name}场景打造的功能特性</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {product.features.map((group, i) => (
              <div key={i} className={`rounded-2xl p-6 ${product.colorLight} border`}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{group.title}</h3>
                <ul className="space-y-3">
                  {group.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-gray-700 text-sm">
                      <svg className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">适用场景</h2>
            <p className="text-gray-600 max-w-xl mx-auto">这些行业和场景正在使用{product.name}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {product.useCases.map((uc, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
                <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center text-white text-sm font-bold mb-4`}>
                  {i + 1}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{uc.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section id="pricing-tiers" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{product.name} 定价方案</h2>
            <p className="text-gray-600 max-w-xl mx-auto">选择最适合您规模的套餐，支持月付和年付</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {product.pricingTiers.map((tier, i) => (
              <div
                key={i}
                className={`rounded-2xl p-8 flex flex-col ${
                  i === 1
                    ? `bg-gradient-to-br ${colors.gradient} text-white shadow-xl`
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {i === 1 && (
                  <div className="inline-flex items-center gap-1 text-xs font-semibold text-yellow-300 mb-3">
                    <span>★</span> 推荐方案
                  </div>
                )}
                <h3 className={`text-xl font-bold ${i === 1 ? 'text-white' : 'text-gray-900'}`}>{tier.name}</h3>
                <p className={`text-sm mb-4 ${i === 1 ? 'text-white/70' : 'text-gray-500'}`}>{tier.description}</p>
                <div className="mb-1">
                  <span className={`text-4xl font-bold ${i === 1 ? 'text-white' : 'text-gray-900'}`}>
                    {tier.price === '面议' ? '面议' : `¥${tier.price}`}
                  </span>
                  {tier.price !== '面议' && (
                    <span className={`text-sm ${i === 1 ? 'text-white/70' : 'text-gray-400'}`}>/月</span>
                  )}
                </div>
                {tier.price !== '面议' && (
                  <p className={`text-xs mb-6 ${i === 1 ? 'text-white/60' : 'text-gray-400'}`}>
                    年付85折，折合 ¥{Math.round(Number(tier.price) * 12 * 0.85).toLocaleString()}/年
                  </p>
                )}
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((f, j) => (
                    <li key={j} className={`flex items-start gap-2 text-sm ${i === 1 ? 'text-white/90' : 'text-gray-600'}`}>
                      <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${i === 1 ? 'text-white' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`mt-auto w-full py-3 rounded-xl text-center font-semibold transition ${
                    i === 1
                      ? 'bg-white text-purple-600 hover:bg-purple-50'
                      : `${colors.btn} text-white`
                  }`}
                >
                  立即咨询
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">常见问题</h2>
          </div>
          <div className="space-y-3">
            {relevantFaqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <button
                  className="w-full text-left px-6 py-4 flex justify-between items-center gap-4 hover:bg-gray-50 transition"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-gray-900">{faq.q}</span>
                  <svg
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className={`py-20 bg-gradient-to-br ${colors.gradient}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">预约{product.name}免费演示</h2>
            <p className="text-white/80">我们的产品顾问将在24小时内与您联系</p>
          </div>
          {formSubmitted ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-xl max-w-lg mx-auto">
              <div className="text-5xl mb-4">🎉</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">预约成功！</h3>
              <p className="text-gray-600 text-sm">我们的顾问将在 <strong>24小时</strong> 内联系您</p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="bg-white rounded-2xl p-8 shadow-xl space-y-4 max-w-lg mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-2">预约免费演示</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">您的姓名 *</label>
                <input required type="text" placeholder="张三" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">公司名称 *</label>
                <input required type="text" placeholder="示例科技有限公司" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系电话 *</label>
                <input required type="tel" placeholder="138-0000-0000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">您希望解决的问题</label>
                <textarea rows={3} placeholder="请简要描述您的需求..." className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  value={formData.need} onChange={(e) => setFormData({ ...formData, need: e.target.value })} />
              </div>
              <button type="submit" className={`w-full py-3 ${colors.btn} text-white rounded-xl font-semibold transition`}>
                提交预约申请
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">智</span>
              </div>
              <span className="text-white font-bold text-lg">智纬AI</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/" className="hover:text-white transition">首页</Link>
              <Link to="/pricing" className="hover:text-white transition">定价</Link>
              <a href="#contact" className="hover:text-white transition">联系我们</a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            © 2026 智纬AI westilt.com 版权所有
          </div>
        </div>
      </footer>
    </div>
  );
}
